/* LT-ProvSums-01 + LT-26 — Tender "Pro Sums" tab (was "Provisional Sums").
 *
 * LT-26 changes:
 *   • Renamed "Provisional Sums" → "Pro Sums" in all user-visible strings.
 *   • New tenders open with 5 library-flagged defaults at £0 instead of 19.
 *   • "Add from Library" picker modal (multi-select) replaces the old
 *     "Add custom line" flow for the common path. "Add custom line"
 *     stays as a secondary action.
 *   • Migration banner for tenders that still have the legacy 19 £0
 *     defaults — Keep / Clear All / dismiss.
 *   • Footer hint pointing at the library is gone — now lives in the
 *     library picker modal itself.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, ArrowRight, Check, CheckSquare, Eraser, ListChecks, Plus,
  RefreshCw, RotateCcw, Square, StickyNote, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp, ukDate } from "../lib/format";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

const cls = (...a) => a.filter(Boolean).join(" ");

// Option A — the figure a line contributes to the estimate/client total.
// A confirmed price overwrites the provisional allowance once entered.
const eff = (it) =>
  it.confirmed_value != null && it.confirmed_value !== ""
    ? Number(it.confirmed_value)
    : Number(it.value || 0);

export default function TenderProvsums({ tenderId, onTenderUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const pendingRef = useRef({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [migrationBusy, setMigrationBusy] = useState(false);
  const [companySplit, setCompanySplit] = useState([]);
  const debounceTimer = useRef(null);

  useEffect(() => {
    api.get("/settings/provsum-defaults")
      .then((r) => setCompanySplit(r.data?.visits || []))
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api.get(`/tenders/${tenderId}/provsums`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load Pro Sums"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Optimistic local mutation + debounced flush ----
  const updateLocal = (itemId, patch) => {
    setData((d) => {
      const items = d.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it));
      // Keep the sub-total live even before the debounced save lands — the
      // subtotal follows the EFFECTIVE figure (confirmed overwrites provisional).
      const affectsTotal = "value" in patch || "confirmed_value" in patch;
      const summary = affectsTotal
        ? { ...d.summary, grand_total: Math.round(items.reduce((s, it) => s + eff(it), 0) * 100) / 100 }
        : d.summary;
      return { ...d, items, summary };
    });
    pendingRef.current = { ...pendingRef.current, [itemId]: { ...(pendingRef.current[itemId] || {}), ...patch } };
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flushAll, 800);
  };

  const flushAll = async () => {
    const pending = pendingRef.current;
    pendingRef.current = {};
    const ids = Object.keys(pending);
    for (const id of ids) {
      try {
        setSavingId(id);
        const r = await api.put(`/tenders/${tenderId}/provsums/${id}`, pending[id]);
        setData(r.data);
        if (r.data?.tender) onTenderUpdate?.(r.data.tender);
      } catch {
        toast.error("Failed to save pro sum");
      } finally {
        setSavingId(null);
      }
    }
  };

  const addCustomLine = async () => {
    try {
      const r = await api.post(`/tenders/${tenderId}/provsums`, {
        description: "", value: 0,
      });
      setData(r.data);
      if (r.data?.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Custom line added");
    } catch {
      toast.error("Failed to add custom line");
    }
  };

  const deleteLine = async (itemId) => {
    if (!window.confirm("Delete this line?")) return;
    try {
      const r = await api.delete(`/tenders/${tenderId}/provsums/${itemId}`);
      setData(r.data);
      if (r.data?.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Line removed");
    } catch {
      toast.error("Failed to delete line");
    }
  };

  const items = data?.items || [];
  const grandTotal = data?.summary?.grand_total || 0;
  const lineCount = items.length;
  const zeroCount = items.filter((it) => eff(it) === 0).length;
  const allZero = items.length > 0 && zeroCount === items.length;
  const bannerDismissed = !!data?.tender?.prov_sums_migration_banner_dismissed;
  const showMigrationBanner = !bannerDismissed && allZero && items.length >= 6;

  const onPickerCommit = async (selectedIds) => {
    if (!selectedIds.length) { setPickerOpen(false); return; }
    try {
      const { data: resp } = await api.post(
        `/tenders/${tenderId}/provsums/bulk-add-from-library`,
        { library_item_ids: selectedIds },
      );
      setData(resp);
      if (resp?.tender) onTenderUpdate?.(resp.tender);
      toast.success(`${resp.added_count} pro sum${resp.added_count === 1 ? "" : "s"} added`);
      setPickerOpen(false);
    } catch {
      toast.error("Failed to add from library");
    }
  };

  const onMigration = async (action) => {
    setMigrationBusy(true);
    try {
      const { data: resp } = await api.post(
        `/tenders/${tenderId}/provsums/migration-banner`,
        { action },
      );
      setData(resp);
      if (resp?.tender) onTenderUpdate?.(resp.tender);
      if (action === "clear_all") toast.success(`Cleared ${resp.cleared_count} legacy line${resp.cleared_count === 1 ? "" : "s"}`);
      if (action === "keep")      toast.success("Pro Sums kept");
    } catch {
      toast.error("Action failed");
    } finally {
      setMigrationBusy(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="text-ink-4 py-8" style={{ fontSize: 12.5 }}>Loading…</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tender-provsums">
      {/* ---- Header strip ---- */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-ink" style={{ fontSize: 18, fontWeight: 600 }}>
            Pro Sums
          </h2>
          <p className="text-ink-3 mt-1" style={{ fontSize: 12 }}>
            Job-specific allowances. Enter a <span className="text-ink-2">confirmed price</span> once a sum is
            firmed up — it overwrites the provisional allowance in the estimate and client quote. Leave a value
            at £0 to skip a category, or remove the line entirely.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12 }}
            data-testid="provsums-refresh-btn"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 12.5 }}
            data-testid="provsums-add-from-library-btn"
          >
            <Plus size={13} /> Add from Library
          </button>
          <button
            onClick={addCustomLine}
            className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md bg-accent text-white shadow-soft hover:bg-accent-deep"
            style={{ fontSize: 12.5, fontWeight: 500 }}
            data-testid="provsums-add-custom-btn"
          >
            <Plus size={14} /> Add custom line
          </button>
        </div>
      </div>

      {/* ---- LT-26 Migration banner (legacy tenders with all-£0 defaults) ---- */}
      {showMigrationBanner ? (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid="provsums-migration-banner"
        >
          <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-amber-900" style={{ fontSize: 12.5, fontWeight: 600 }}>
              Legacy Pro Sums defaults detected
            </div>
            <p className="text-amber-800 mt-0.5" style={{ fontSize: 11.5 }}>
              This tender has {items.length} Pro Sums at £0 from the older 19-default
              seed. New tenders now start with just 5 standard categories. Keep the
              full list, or clear the £0 defaults and add only what's relevant.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => onMigration("keep")}
                disabled={migrationBusy}
                className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-60"
                style={{ fontSize: 11.5 }}
                data-testid="provsums-migration-keep-btn"
              >
                <Check size={11} /> Keep all
              </button>
              <button
                onClick={() => onMigration("clear_all")}
                disabled={migrationBusy}
                className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-amber-300 bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60"
                style={{ fontSize: 11.5 }}
                data-testid="provsums-migration-clear-btn"
              >
                <Eraser size={11} /> Clear £0 defaults
              </button>
            </div>
          </div>
          <button
            onClick={() => onMigration("dismiss")}
            disabled={migrationBusy}
            className="text-amber-700 hover:text-amber-900 p-1 rounded disabled:opacity-60"
            title="Dismiss"
            data-testid="provsums-migration-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      {/* ---- Summary card ---- */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Lines" value={lineCount} icon={ListChecks} testId="provsums-summary-lines" />
        <SummaryCard
          label="Subtotal"
          value={gbp(grandTotal)}
          icon={AlertCircle}
          testId="provsums-summary-subtotal"
        />
        <SummaryCard
          label="Last updated"
          value={data?.tender?.updated_at ? ukDate(data.tender.updated_at) : "—"}
          icon={RefreshCw}
          testId="provsums-summary-updated"
        />
      </div>

      {/* ---- Lines table ---- */}
      <div className="rounded-lg border border-line bg-surface overflow-hidden">
        <table className="w-full" style={{ fontSize: 12.5 }}>
          <thead>
            <tr className="border-b border-line bg-surface-2 text-left">
              <th className="col-head px-3 py-2" style={{ fontSize: 10, width: 50 }}>#</th>
              <th className="col-head px-3 py-2" style={{ fontSize: 10 }}>Description</th>
              <th className="col-head px-3 py-2 text-right" style={{ fontSize: 10, width: 150 }}>Provisional (£)</th>
              <th className="col-head px-3 py-2 text-right" style={{ fontSize: 10, width: 190 }}>Confirmed (£)</th>
              <th className="col-head px-3 py-2 text-center" style={{ fontSize: 10, width: 80 }}>Notes</th>
              <th className="col-head px-3 py-2 text-center" style={{ fontSize: 10, width: 120 }}>Visit split</th>
              <th className="col-head px-3 py-2" style={{ fontSize: 10, width: 80 }}>Source</th>
              <th className="col-head px-3 py-2 text-right" style={{ fontSize: 10, width: 50 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <ProvsumRow
                key={it.id}
                item={it}
                index={idx + 1}
                isSaving={savingId === it.id}
                companySplit={companySplit}
                onChange={(patch) => updateLocal(it.id, patch)}
                onDelete={() => deleteLine(it.id)}
              />
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-ink-4 py-8" style={{ fontSize: 12 }}>
                  No Pro Sums on this tender yet. Use "Add from Library" or "Add custom line" above.
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-line bg-surface-2">
                <td colSpan={3} className="px-3 py-2 text-right text-ink" style={{ fontSize: 12, fontWeight: 600 }}>
                  Subtotal
                </td>
                <td className="px-3 py-2 text-right text-ink" style={{ fontSize: 13, fontWeight: 600 }} data-testid="provsums-subtotal-cell">
                  {gbp(grandTotal)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ---- £0 warning (subtle, only when relevant) ---- */}
      {zeroCount > 0 && !showMigrationBanner ? (
        <p
          className="text-amber-700 flex items-center gap-1.5"
          style={{ fontSize: 11.5 }}
          data-testid="provsums-zero-warning"
        >
          <AlertCircle size={12} />
          {zeroCount} pro sum line{zeroCount === 1 ? "" : "s"} at £0 — these
          contribute nothing to the client total. Enter values or remove.
        </p>
      ) : null}

      {/* ---- Library picker modal ---- */}
      {pickerOpen ? (
        <ProSumLibraryPicker
          tenderItems={items}
          onClose={() => setPickerOpen(false)}
          onCommit={onPickerCommit}
        />
      ) : null}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, testId }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 col-head" style={{ fontSize: 10 }}>
        {Icon ? <Icon size={11} /> : null}
        {label}
      </div>
      <div className="text-ink mt-1" style={{ fontSize: 18, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function ProvsumRow({ item, index, isSaving, companySplit, onChange, onDelete }) {
  const [localDesc, setLocalDesc] = useState(item.description || "");
  const [localValue, setLocalValue] = useState(
    item.value != null ? String(item.value) : "",
  );
  const [localConfirmed, setLocalConfirmed] = useState(
    item.confirmed_value != null ? String(item.confirmed_value) : "",
  );
  // Sync from upstream when server replaces data with fresh items.
  useEffect(() => { setLocalDesc(item.description || ""); }, [item.description]);
  useEffect(() => {
    setLocalValue(item.value != null ? String(item.value) : "");
  }, [item.value]);
  useEffect(() => {
    setLocalConfirmed(item.confirmed_value != null ? String(item.confirmed_value) : "");
  }, [item.confirmed_value]);

  const overridden = item.description_overridden;
  const isCustom = item.is_custom;
  const provisional = Number(item.value || 0);
  const confirmedSet = item.confirmed_value != null;
  const variance = confirmedSet ? Number(item.confirmed_value) - provisional : 0;

  return (
    <tr className="border-b border-line/60 last:border-0 hover:bg-surface-2/40" data-testid={`provsum-row-${index}`}>
      <td className="px-3 py-2 text-ink-4" style={{ fontSize: 11 }}>{index}</td>
      <td className="px-3 py-1.5">
        <input
          type="text"
          value={localDesc}
          onChange={(e) => setLocalDesc(e.target.value)}
          onBlur={() => {
            if (localDesc !== item.description) onChange({ description: localDesc });
          }}
          placeholder={isCustom ? "e.g. Bespoke joinery" : ""}
          className={cls(
            "w-full bg-transparent border-0 text-ink focus:outline-none focus:ring-1 focus:ring-accent rounded px-1.5 py-1",
            overridden && "italic",
          )}
          style={{ fontSize: 12.5 }}
          data-testid={`provsum-desc-${index}`}
        />
        {overridden && (
          <span className="text-ink-4 ml-2" style={{ fontSize: 10 }}>
            (edited)
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right">
        <input
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={() => {
            const v = Number(localValue || 0);
            if (v !== Number(item.value || 0)) onChange({ value: v });
          }}
          min={0}
          step={1}
          placeholder="0"
          className={cls(
            "w-full bg-transparent border-0 text-right focus:outline-none focus:ring-1 focus:ring-accent rounded px-1.5 py-1 tabular-nums",
            confirmedSet ? "text-ink-4 line-through" : "text-ink",
          )}
          style={{ fontSize: 12.5 }}
          title={confirmedSet ? "Superseded by the confirmed price" : "Provisional allowance"}
          data-testid={`provsum-value-${index}`}
        />
      </td>
      <td className="px-3 py-1.5 text-right">
        <div className="flex items-center justify-end gap-1">
          <input
            type="number"
            value={localConfirmed}
            onChange={(e) => setLocalConfirmed(e.target.value)}
            onBlur={() => {
              const raw = (localConfirmed || "").trim();
              if (raw === "") {
                if (item.confirmed_value != null) onChange({ confirmed_value: null });
              } else {
                const v = Number(raw);
                if (v !== Number(item.confirmed_value ?? NaN)) onChange({ confirmed_value: v });
              }
            }}
            min={0}
            step={1}
            placeholder="—"
            className="w-full bg-transparent border-0 text-ink text-right focus:outline-none focus:ring-1 focus:ring-accent rounded px-1.5 py-1 tabular-nums"
            style={{ fontSize: 12.5 }}
            title="Enter the confirmed cost — it overwrites the provisional allowance in the estimate and client quote"
            data-testid={`provsum-confirmed-${index}`}
          />
          {confirmedSet ? (
            <button
              type="button"
              onClick={() => { setLocalConfirmed(""); onChange({ confirmed_value: null }); }}
              className="text-ink-4 hover:text-note-red p-1 rounded shrink-0"
              title="Remove confirmed price — revert to the provisional allowance"
              aria-label="Revert to provisional allowance"
              data-testid={`provsum-confirmed-revert-${index}`}
            >
              <RotateCcw size={12} />
            </button>
          ) : null}
        </div>
        {confirmedSet ? (
          <div
            className={cls(
              "mt-0.5",
              variance > 0 ? "text-note-red" : variance < 0 ? "text-emerald-700" : "text-ink-4",
            )}
            style={{ fontSize: 10 }}
            data-testid={`provsum-variance-${index}`}
          >
            {variance === 0
              ? "On allowance"
              : `${variance > 0 ? "+" : "−"}${gbp(Math.abs(variance))} vs allowance`}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-1.5 text-center">
        <NotesPopover item={item} onChange={onChange} index={index} />
      </td>
      <td className="px-3 py-1.5 text-center">
        <SplitPopover item={item} companySplit={companySplit} onChange={onChange} index={index} />
      </td>
      <td className="px-3 py-1.5">
        {isCustom ? (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded"
            style={{ fontSize: 10, background: "#FEF3E2", color: "#7A4C0A" }}
          >
            CUSTOM
          </span>
        ) : (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded"
            style={{ fontSize: 10, background: "#E8EFE9", color: "#2F4F3E" }}
          >
            LIBRARY
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right">
        <button
          onClick={onDelete}
          className="text-ink-4 hover:text-note-red p-1 rounded"
          aria-label="Delete line"
          data-testid={`provsum-delete-${index}`}
          title={"Remove this line"}
        >
          <Trash2 size={13} />
        </button>
        {isSaving && (
          <span className="text-ink-4 ml-1" style={{ fontSize: 10 }}>saving…</span>
        )}
      </td>
    </tr>
  );
}

const DEFAULT_SPLIT_FALLBACK = [
  { name: "First fix", pct: 40 },
  { name: "Second fix", pct: 40 },
  { name: "Final fix", pct: 20 },
];

// Phase 1 follow-on — per-prov-sum visit-split override editor. Independent of
// the company default; when set it snapshots on the tender line and is
// validated to total 100%. "Default" = inherit the company setting.
function SplitPopover({ item, companySplit, onChange, index }) {
  const [open, setOpen] = useState(false);
  const hasOverride = Array.isArray(item.visit_split) && item.visit_split.length > 0;
  const companyVisits = companySplit && companySplit.length ? companySplit : DEFAULT_SPLIT_FALLBACK;
  const [mode, setMode] = useState(hasOverride ? "custom" : "default");
  const [visits, setVisits] = useState(hasOverride ? item.visit_split : companyVisits);

  useEffect(() => {
    const ov = Array.isArray(item.visit_split) && item.visit_split.length > 0;
    setMode(ov ? "custom" : "default");
    setVisits(ov ? item.visit_split : companyVisits);
  }, [item.visit_split, companySplit]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = Math.round(visits.reduce((s, v) => s + (Number(v.pct) || 0), 0) * 100) / 100;
  const customValid =
    visits.length >= 2 && visits.length <= 3 &&
    Math.abs(total - 100) < 0.01 && visits.every((v) => (v.name || "").trim());
  const canSave = mode === "default" || customValid;

  const upd = (i, patch) => setVisits((vs) => vs.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const setTwo = () => setVisits([{ name: "First fix", pct: 60 }, { name: "Second fix", pct: 40 }]);
  const setThree = () => setVisits([...DEFAULT_SPLIT_FALLBACK]);

  const save = () => {
    if (mode === "default") onChange({ visit_split: null });
    else onChange({ visit_split: visits.map((v) => ({ name: v.name.trim(), pct: Number(v.pct) || 0 })) });
    setOpen(false);
  };

  const chip = hasOverride ? item.visit_split.map((v) => Math.round(v.pct)).join("/") : "Default";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cls(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md",
            hasOverride ? "text-white bg-indigo-600" : "text-ink-4 hover:bg-surface-2 border border-line",
          )}
          style={{ fontSize: 10 }}
          data-testid={`provsum-split-${index}`}
          title={hasOverride ? "Custom visit split (overrides company default)" : "Using company default split"}
        >
          {chip}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" style={{ fontSize: 12 }}>
        <div className="col-head mb-2" style={{ fontSize: 10 }}>Subcontractor visit split</div>
        <div className="flex gap-1.5 mb-2">
          <button
            onClick={() => setMode("default")}
            data-testid={`provsum-split-mode-default-${index}`}
            className={cls("h-7 px-2.5 rounded-md border text-xs",
              mode === "default" ? "bg-accent text-white border-accent" : "bg-surface text-ink-2 border-line")}
          >
            Company default
          </button>
          <button
            onClick={() => setMode("custom")}
            data-testid={`provsum-split-mode-custom-${index}`}
            className={cls("h-7 px-2.5 rounded-md border text-xs",
              mode === "custom" ? "bg-accent text-white border-accent" : "bg-surface text-ink-2 border-line")}
          >
            Custom for this line
          </button>
        </div>

        {mode === "default" ? (
          <div className="text-ink-3 mb-3" style={{ fontSize: 11.5 }}>
            Inherits the company default:{" "}
            <span className="text-ink-2">{companyVisits.map((v) => `${v.name} ${Math.round(v.pct)}%`).join(" · ")}</span>
          </div>
        ) : (
          <div className="mb-2">
            <div className="flex gap-1.5 mb-2">
              <button onClick={setTwo} className="text-xs text-accent hover:underline" data-testid={`provsum-split-two-${index}`}>Two-way</button>
              <span className="text-ink-4">·</span>
              <button onClick={setThree} className="text-xs text-accent hover:underline" data-testid={`provsum-split-three-${index}`}>Three-way</button>
            </div>
            {visits.map((v, i) => (
              <div key={i} className="flex items-center gap-1.5 mb-1.5">
                <input
                  value={v.name}
                  onChange={(e) => upd(i, { name: e.target.value })}
                  className="flex-1 h-7 px-2 rounded border border-line bg-surface text-ink" style={{ fontSize: 11.5 }}
                  data-testid={`provsum-split-name-${index}-${i}`}
                />
                <input
                  type="number" value={v.pct}
                  onChange={(e) => upd(i, { pct: Number(e.target.value) || 0 })}
                  className="w-16 h-7 px-2 rounded border border-line bg-surface text-ink text-right tabular-nums" style={{ fontSize: 11.5 }}
                  data-testid={`provsum-split-pct-${index}-${i}`}
                />
                <span className="text-ink-3" style={{ fontSize: 11 }}>%</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 mt-1" data-testid={`provsum-split-total-${index}`}>
              <span className="col-head" style={{ fontSize: 10 }}>Total</span>
              <span className="font-mono tnum" style={{ fontSize: 12, fontWeight: 700, color: customValid ? "#2F4F3E" : "#A63A2E" }}>{total}%</span>
              {!customValid && <span style={{ fontSize: 10.5, color: "#A63A2E" }}>must be 100% (2–3 visits)</span>}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button onClick={() => setOpen(false)} className="text-xs text-ink-3 hover:text-ink px-2 py-1">Cancel</button>
          <button
            onClick={save} disabled={!canSave}
            data-testid={`provsum-split-save-${index}`}
            className={cls("text-xs px-3 py-1 rounded-md", canSave ? "bg-accent text-white" : "bg-line text-ink-4 cursor-not-allowed")}
          >
            {mode === "default" ? "Use default" : "Save split"}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function NotesPopover({ item, onChange, index }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(item.notes || "");
  useEffect(() => { setDraft(item.notes || ""); }, [item.notes]);
  const hasNote = !!(item.notes && item.notes.trim());
  const save = () => {
    if ((draft || "").trim() !== (item.notes || "").trim()) {
      onChange({ notes: draft.trim() || null });
    }
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cls(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md",
            hasNote ? "text-accent bg-surface-2" : "text-ink-4 hover:bg-surface-2",
          )}
          style={{ fontSize: 10 }}
          data-testid={`provsum-notes-${index}`}
        >
          <StickyNote size={12} />
          {hasNote ? "· 1" : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3" style={{ fontSize: 12 }}>
        <div className="col-head mb-2" style={{ fontSize: 10 }}>Note</div>
        {hasNote && item.note_author_name && (
          <div className="text-ink-4 mb-2" style={{ fontSize: 10 }}>
            {item.note_author_name}{" · "}
            {item.note_updated_at ? ukDate(item.note_updated_at) : "—"}
          </div>
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          className="w-full border border-line rounded-md px-2 py-1.5 text-ink focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="Add internal note about this allowance…"
          style={{ fontSize: 12 }}
        />
        <div className="flex justify-end mt-2 gap-2">
          <button
            onClick={() => setOpen(false)}
            className="h-7 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
            style={{ fontSize: 11 }}
          >
            Cancel
          </button>
          <button
            onClick={save}
            className="h-7 px-3 rounded-md bg-accent text-white hover:bg-accent-deep"
            style={{ fontSize: 11 }}
          >
            Save
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}


/* ============================================================
 * LT-26 — Pro Sum library picker modal (multi-select).
 *
 * Loads the library on open; renders a checkable list with library items
 * that are NOT already on the tender (de-duped by library_item_id).
 * Commit calls /provsums/bulk-add-from-library with the selected IDs.
 * ========================================================== */
function ProSumLibraryPicker({ tenderItems, onClose, onCommit }) {
  const [lib, setLib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/provsum-library")
      .then((r) => setLib(r.data || []))
      .catch(() => toast.error("Failed to load Pro Sums library"))
      .finally(() => setLoading(false));
  }, []);

  const alreadyOnTenderIds = useMemo(() => {
    const s = new Set();
    for (const it of (tenderItems || [])) {
      if (it.library_item_id) s.add(it.library_item_id);
    }
    return s;
  }, [tenderItems]);

  const visible = useMemo(() => {
    if (!lib) return [];
    const f = filter.trim().toLowerCase();
    return lib
      .filter((row) => row.is_active !== false)
      .filter((row) => !alreadyOnTenderIds.has(row.id))
      .filter((row) => !f || (row.description || "").toLowerCase().includes(f))
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [lib, filter, alreadyOnTenderIds]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSelectAll = () => {
    setSelected(new Set(visible.map((r) => r.id)));
  };
  const onClearSelection = () => setSelected(new Set());

  const commit = async () => {
    if (!selected.size) return;
    setBusy(true);
    try {
      await onCommit([...selected]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="provsum-library-picker"
    >
      <div
        className="bg-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h3 className="text-ink" style={{ fontSize: 16, fontWeight: 600 }}>
              Add Pro Sums from Library
            </h3>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
              Items already on the tender are hidden. Pick one or more, then click Add.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-3 hover:text-ink p-1.5 rounded hover:bg-surface-2"
            title="Close"
            data-testid="provsum-picker-close-btn"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search + select-all */}
        <div className="px-5 py-3 border-b border-line flex items-center gap-3">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            className="flex-1 border border-line rounded-md px-3 py-1.5 text-ink focus:outline-none focus:ring-1 focus:ring-accent"
            style={{ fontSize: 12.5 }}
            data-testid="provsum-picker-filter"
          />
          <button
            onClick={onSelectAll}
            disabled={visible.length === 0}
            className="text-ink-3 hover:text-ink underline disabled:opacity-50"
            style={{ fontSize: 11.5 }}
            data-testid="provsum-picker-select-all"
          >
            Select all
          </button>
          {selected.size > 0 ? (
            <button
              onClick={onClearSelection}
              className="text-ink-3 hover:text-ink underline"
              style={{ fontSize: 11.5 }}
              data-testid="provsum-picker-clear-selection"
            >
              Clear ({selected.size})
            </button>
          ) : null}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="text-ink-4 px-3 py-4" style={{ fontSize: 12 }}>Loading library…</div>
          ) : visible.length === 0 ? (
            <div className="text-ink-4 px-3 py-6 text-center" style={{ fontSize: 12.5 }}>
              {alreadyOnTenderIds.size > 0
                ? "All library items are already on this tender."
                : "Library is empty."}
            </div>
          ) : (
            <ul className="space-y-0.5" data-testid="provsum-picker-list">
              {visible.map((row) => {
                const isOn = selected.has(row.id);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => toggle(row.id)}
                      className={cls(
                        "w-full flex items-center gap-3 px-3 py-2 rounded-md text-left",
                        "hover:bg-surface-2",
                        isOn && "bg-accent/5",
                      )}
                      data-testid={`provsum-picker-row-${row.id}`}
                    >
                      {isOn ? (
                        <CheckSquare size={15} className="text-accent shrink-0" />
                      ) : (
                        <Square size={15} className="text-ink-4 shrink-0" />
                      )}
                      <span className="flex-1 text-ink" style={{ fontSize: 13 }}>
                        {row.description}
                      </span>
                      {row.is_default_for_new_tenders ? (
                        <span
                          className="px-1.5 py-0.5 rounded text-amber-700 bg-amber-50 border border-amber-200"
                          style={{ fontSize: 9.5, letterSpacing: "0.05em", fontWeight: 500 }}
                        >
                          DEFAULT
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-5 py-3 border-t border-line flex items-center justify-between bg-surface-2/40">
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>
            {selected.size === 0
              ? "No items selected"
              : `${selected.size} item${selected.size === 1 ? "" : "s"} selected`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2"
              style={{ fontSize: 12 }}
              data-testid="provsum-picker-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={commit}
              disabled={selected.size === 0 || busy}
              className="h-8 px-4 rounded-md bg-accent text-white shadow-soft hover:bg-accent-deep disabled:opacity-50 inline-flex items-center gap-1.5"
              style={{ fontSize: 12.5, fontWeight: 500 }}
              data-testid="provsum-picker-commit-btn"
            >
              <ArrowRight size={13} /> Add to tender
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
